import { Injectable, WritableSignal, effect, signal } from '@angular/core';
import { CookieConsentService } from './cookie-consent.service';
import { SettingsSeedService } from './settings-seed.service';

export type NotificationSize = 'small' | 'normal' | 'large' | 'massive';
export type NotificationPosition = 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center';
export type FontScale = 'small' | 'normal' | 'large';
export type TableDensity = 'compact' | 'normal' | 'comfortable';
export type MotionPref = 'normal' | 'reduced' | 'off';

export interface UiSettings {
	notificationSize: NotificationSize;
	notificationPosition: NotificationPosition;
	notificationBlinking: boolean;
	fontScale: FontScale;
	tableDensity: TableDensity;
	motion: MotionPref;
	compactSpacing: boolean;
	highContrast: boolean;
	tableGridlines: boolean;
}

const STORAGE_KEY = 'appUiSettings';

const DEFAULT_SETTINGS: UiSettings = {
	notificationSize: 'normal',
	notificationPosition: 'top-right',
	notificationBlinking: true,
	fontScale: 'normal',
	tableDensity: 'normal',
	motion: 'normal',
	compactSpacing: false,
	highContrast: false,
	tableGridlines: true
};

@Injectable({
	providedIn: 'root'
})
export class UiSettingsService {
	settings: WritableSignal<UiSettings> = signal<UiSettings>({ ...DEFAULT_SETTINGS });

	constructor(
		private cookieConsentService: CookieConsentService,
		private settingsSeedService: SettingsSeedService
	) {
		this.loadFromStorage();

		// Registering here (rather than in the seed service) is the pattern any future settings
		// service should follow — the seed mechanism picks up new keys/sources automatically.
		this.settingsSeedService.register<Partial<UiSettings>>({
			key: 'ui',
			getState: () => this.settings(),
			applyState: (state) => this.settings.set({ ...DEFAULT_SETTINGS, ...state })
		});

		effect(() => {
			const settings = this.settings();
			if (this.cookieConsentService.consentGiven()) {
				this.saveToStorage(settings);
			}
		});

		effect(() => {
			this.applyToDom(this.settings());
		});
	}

	update<K extends keyof UiSettings>(key: K, value: UiSettings[K]): void {
		this.settings.update((state) => ({ ...state, [key]: value }));
	}

	reset(): void {
		this.settings.set({ ...DEFAULT_SETTINGS });
	}

	private applyToDom(settings: UiSettings): void {
		const root = document.documentElement;
		root.setAttribute('data-notif-size', settings.notificationSize);
		root.setAttribute('data-notif-position', settings.notificationPosition);
		root.setAttribute('data-notif-blink', String(settings.notificationBlinking));
		root.setAttribute('data-font-scale', settings.fontScale);
		root.setAttribute('data-table-density', settings.tableDensity);
		root.setAttribute('data-motion', settings.motion);
		root.setAttribute('data-compact', String(settings.compactSpacing));
		root.setAttribute('data-high-contrast', String(settings.highContrast));
		root.setAttribute('data-table-gridlines', String(settings.tableGridlines));
	}

	private saveToStorage(settings: UiSettings): void {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
	}

	private loadFromStorage(): void {
		if (!this.cookieConsentService.consentGiven()) return;

		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return;

		try {
			const parsed = JSON.parse(stored) as Partial<UiSettings>;
			this.settings.set({ ...DEFAULT_SETTINGS, ...parsed });
		} catch (e) {
			console.error('Failed to parse appUiSettings from storage:', e);
		}
	}
}
